/**
 * Findings Page - Compliance Gaps and Security Vulnerabilities
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, CheckCircle2, Shield, Calendar, FileText, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FindingsPage() {
  const [findings, setFindings] = useState([]);
  const [pentestFindings, setPentestFindings] = useState([]);
  const [pentestReports, setPentestReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchFindings = async () => {
      try {
        const [findingsRes, summaryRes, pentestRes] = await Promise.all([
          fetch(`${API_URL}/api/findings/?limit=50`, { credentials: 'include' }),
          fetch(`${API_URL}/api/findings/summary`, { credentials: 'include' }),
          fetch(`${API_URL}/api/pentest/reports`, { credentials: 'include' }),
        ]);

        if (findingsRes.ok) setFindings((await findingsRes.json()).findings || []);
        if (summaryRes.ok) setSummary(await summaryRes.json());

        if (pentestRes.ok) {
          const pentestData = await pentestRes.json();
          const reports = pentestData.reports || [];
          setPentestReports(reports);

          const allPentestFindings = [];
          for (const report of reports) {
            try {
              const reportRes = await fetch(
                `${API_URL}/api/pentest/reports/${report.report_id}`,
                { credentials: 'include' }
              );
              if (reportRes.ok) {
                const reportData = await reportRes.json();
                (reportData.findings || []).forEach((f) => {
                  allPentestFindings.push({
                    ...f,
                    source: 'pentest',
                    report_label: report.label || report.filename,
                    report_id: report.report_id,
                  });
                });
              }
            } catch (err) {
              console.warn(`Could not load findings for ${report.report_id}:`, err);
            }
          }
          setPentestFindings(allPentestFindings);
        }
      } catch (error) {
        console.error('Failed to fetch findings:', error);
        toast.error('Failed to load findings');
      } finally {
        setLoading(false);
      }
    };
    fetchFindings();
  }, []);

  const displayFindings = activeTab === 'all' 
    ? [...findings, ...pentestFindings.map(pf => ({
        finding_id: `PT-${pf.title?.slice(0, 20)}`,
        title: pf.title,
        severity: pf.severity,
        status: 'open',
        environment: 'Security Scan',
        source: 'pentest',
        cvss_score: pf.cvss_score,
        control_ids: pf.control_ids,
      }))]
    : activeTab === 'compliance' 
      ? findings 
      : pentestFindings.map(pf => ({
          finding_id: `PT-${pf.title?.slice(0, 20)}`,
          title: pf.title,
          severity: pf.severity,
          status: 'open',
          environment: 'Security Scan',
          source: 'pentest',
          cvss_score: pf.cvss_score,
          control_ids: pf.control_ids,
        }));

  return (
    <div className="findings-page">
      <div className="page-header">
        <div>
          <h1>Findings Tracker</h1>
          <p>Track and remediate compliance gaps and security vulnerabilities</p>
        </div>
        <button className="btn-primary" data-testid="create-finding-btn">
          <Plus size={16} /> Create Finding
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card critical">
          <span className="summary-label">Critical/High Open</span>
          <span className="summary-value">
            {(summary?.open_critical || 0) + pentestFindings.filter(f => f.severity === 'critical' || f.severity === 'high').length}
          </span>
        </div>
        <div className="summary-card warning">
          <span className="summary-label">Compliance Findings</span>
          <span className="summary-value">{findings.length}</span>
        </div>
        <div className="summary-card info">
          <span className="summary-label">Security Scan Findings</span>
          <span className="summary-value">{pentestFindings.length}</span>
        </div>
        <div className="summary-card success">
          <span className="summary-label">Resolved</span>
          <span className="summary-value">{summary?.by_status?.resolved || 0}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="findings-tabs">
        <button 
          className={activeTab === 'all' ? 'active' : ''} 
          onClick={() => setActiveTab('all')}
          data-testid="tab-all-findings"
        >
          All Findings ({findings.length + pentestFindings.length})
        </button>
        <button 
          className={activeTab === 'compliance' ? 'active' : ''} 
          onClick={() => setActiveTab('compliance')}
          data-testid="tab-compliance-findings"
        >
          Compliance ({findings.length})
        </button>
        <button 
          className={activeTab === 'security' ? 'active' : ''} 
          onClick={() => setActiveTab('security')}
          data-testid="tab-security-findings"
        >
          Security Scans ({pentestFindings.length})
        </button>
      </div>

      <div className="findings-list">
        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : displayFindings.length > 0 ? (
          displayFindings.map((finding, idx) => (
            <Card key={`${finding.finding_id}-${idx}`} className="finding-card">
              <div className="finding-header">
                <div>
                  <div className="finding-id">
                    {finding.source === 'pentest' ? (
                      <Badge color="#7c3aed" style={{ marginRight: 8 }}>Security Scan</Badge>
                    ) : null}
                    {finding.finding_id}
                  </div>
                  <div className="finding-title">{finding.title}</div>
                  <div className="finding-meta">
                    {finding.environment || 'Unknown'} 
                    {finding.cvss_score ? ` — CVSS: ${finding.cvss_score}` : ''}
                    {finding.control_ids?.length ? ` — Controls: ${finding.control_ids.join(', ')}` : ''}
                    {finding.assigned_to ? ` — Assigned to ${finding.assigned_to}` : ''}
                  </div>
                </div>
                <div className="finding-badges">
                  <Badge color={
                    finding.severity === 'critical' ? '#dc2626' : 
                    finding.severity === 'high' ? '#ea580c' : 
                    finding.severity === 'medium' ? '#d97706' :
                    finding.severity === 'low' ? '#2563eb' : '#6b7280'
                  }>
                    {finding.severity}
                  </Badge>
                  <Badge color={
                    finding.status === 'resolved' ? '#059669' :
                    finding.status === 'in_progress' ? '#d97706' : '#dc2626'
                  }>{finding.status?.replace(/_/g, ' ') || 'open'}</Badge>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <CleanPostureCard
            pentestReports={pentestReports}
            activeTab={activeTab}
          />
        )}
      </div>
    </div>
  );
}

// ── Clean security posture card ────────────────────────────────────────
// Renders meaningful "no findings" evidence rather than a generic empty
// state. Lists the most recent pen tests with their scan date and clean
// status, so users can demonstrate to auditors that the absence of
// findings is the result of active scanning, not skipped work.
function CleanPostureCard({ pentestReports, activeTab }) {
  const reports = (pentestReports || []).slice(0, 5); // most recent 5
  const latest = reports[0];

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return '—'; }
  };

  const totalScans = reports.length;
  const tabLabel = activeTab === 'compliance' ? 'compliance' : activeTab === 'security' ? 'security' : '';

  return (
    <Card className="clean-posture" data-testid="clean-posture-card" style={{ padding: 32 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
        <div
          style={{
            background: 'rgba(5, 150, 105, 0.12)',
            border: '1px solid rgba(5, 150, 105, 0.35)',
            borderRadius: 12,
            padding: 16,
            flexShrink: 0,
          }}
        >
          <CheckCircle2 size={36} color="#059669" />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, color: '#059669', fontSize: 20 }}>
            No open {tabLabel} findings
          </h3>
          <p style={{ marginTop: 6, color: '#cbd5e1' }}>
            {totalScans > 0
              ? `Backed by ${totalScans} security scan${totalScans === 1 ? '' : 's'} on file — every accepted finding has been remediated or marked resolved. This is positive audit evidence: scanning was performed and nothing was outstanding at last check.`
              : 'Upload a pen test report or accessibility audit on the Security Scans page to populate this section.'}
          </p>

          {latest && (
            <div
              style={{
                marginTop: 20,
                padding: 16,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Shield size={16} color="#10b981" />
                <strong style={{ color: '#e4e6ee' }}>Most recent security scan</strong>
                <Badge color="#059669" style={{ marginLeft: 'auto' }}>Clean</Badge>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, fontSize: 13 }}>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>Report</div>
                  <div style={{ color: '#e4e6ee', wordBreak: 'break-word' }}>{latest.label || latest.filename}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>Scanner</div>
                  <div style={{ color: '#e4e6ee' }}>{latest.scanner || 'Manual upload'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>Date</div>
                  <div style={{ color: '#e4e6ee', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Calendar size={12} /> {formatDate(latest.scan_date || latest.uploaded_at)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {reports.length > 1 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 8 }}>
                Recent scan history
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {reports.slice(1).map((r) => (
                  <div
                    key={r.report_id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 6,
                      background: 'rgba(255,255,255,0.02)',
                      fontSize: 12,
                    }}
                  >
                    <FileText size={14} color="#6b7280" />
                    <span style={{ flex: 1, color: '#cbd5e1' }}>{r.label || r.filename}</span>
                    <span style={{ color: '#6b7280' }}>{formatDate(r.scan_date || r.uploaded_at)}</span>
                    <Badge color={r.total_findings > 0 ? '#d97706' : '#059669'}>
                      {r.total_findings > 0 ? `${r.total_findings} findings` : 'Clean'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
            <Link
              to="/pentest"
              data-testid="link-pentest-page"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: '#60a5fa', textDecoration: 'none', fontSize: 13,
              }}
            >
              View all security scans <ExternalLink size={12} />
            </Link>
            <Link
              to="/accessibility"
              data-testid="link-accessibility-page"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: '#60a5fa', textDecoration: 'none', fontSize: 13,
              }}
            >
              View accessibility audits <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );
}
